import React, { useEffect, useState, useRef, useMemo } from "react";

function Chat({ socket, username, room }) {
  const [currentMessage, setCurrentMessage] = useState("");
  const [messageList, setMessageList] = useState([]);
  const [typingStatus, setTypingStatus] = useState(""); // Stores username now
  const messagesEndRef = useRef(null);
  const stopTypingLogic = useRef();

  useEffect(() => {
    stopTypingLogic.current = () => {
      socket.emit("stop_typing", { room, username });
    };
  }, [socket, room, username]);

  const stopTyping = useMemo(
    () =>
      debounce(() => {
        if (stopTypingLogic.current) {
          stopTypingLogic.current();
        }
      }, 2000),
    [],
  );

  const sendMessage = async () => {
    if (currentMessage !== "") {
      const messageData = {
        room: room,
        author: username,
        message: currentMessage,
        time: new Date().getHours() + ":" + new Date().getMinutes(),
      };

      await socket.emit("send_message", messageData);
      socket.emit("stop_typing", { room, username });
      stopTyping.cancel();

      setMessageList((list) => [...list, messageData]);
      setCurrentMessage("");
    }
  };

  const handleTyping = (event) => {
    setCurrentMessage(event.target.value);
    if (event.target.value !== "") {
      socket.emit("typing", { room, username });
      stopTyping();
    }
  };

  useEffect(() => {
    const handleReceiveMessage = (data) => {
      setMessageList((list) => [...list, data]);
    };

    const handleLoadMessages = (messages) => {
      setMessageList(messages);
    };

    const handleDisplayTyping = (data) => {
      // UPDATED: Store just the username
      setTypingStatus(data.username);

      const fallbackTimer = setTimeout(() => {
        setTypingStatus("");
      }, 5000);

      handleDisplayTyping.timerId = fallbackTimer;
    };

    const handleHideTyping = () => {
      setTypingStatus("");
      if (handleDisplayTyping.timerId) {
        clearTimeout(handleDisplayTyping.timerId);
      }
    };

    socket.on("receive_message", handleReceiveMessage);
    socket.on("load_messages", handleLoadMessages);
    socket.on("display_typing", handleDisplayTyping);
    socket.on("hide_typing", handleHideTyping);

    return () => {
      socket.off("receive_message", handleReceiveMessage);
      socket.off("load_messages", handleLoadMessages);
      socket.off("display_typing", handleDisplayTyping);
      socket.off("hide_typing", handleHideTyping);
    };
  }, [socket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messageList, typingStatus]);

  return (
    <div className="chat-container">
      <div className="chat-header">
        <p>Live Chat | Room: {room}</p>
      </div>
      <div className="chat-body">
        {messageList.map((msg, index) => {
          const isMe = msg.author === username;
          return (
            <div className="message" id={isMe ? "you" : "other"} key={index}>
              <div className="message-content">
                <p>{msg.message}</p>
              </div>
              <div className="message-meta">
                <p id="time">{msg.time}</p>
                <p id="author">{msg.author}</p>
              </div>
            </div>
          );
        })}

        {/* UPDATED: Render username in bold */}
        {typingStatus && (
          <div className="message" id="other">
            <div
              className="message-content"
              style={{
                backgroundColor: "transparent",
                color: "var(--text-muted)",
                padding: "0",
                fontStyle: "italic",
              }}
            >
              <p style={{ fontSize: "12px" }}>
                <b>{typingStatus}</b> is typing...
              </p>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="chat-footer">
        <input
          type="text"
          value={currentMessage}
          placeholder="Hey..."
          onChange={handleTyping}
          onKeyPress={(event) => {
            event.key === "Enter" && sendMessage();
          }}
        />
        <button onClick={sendMessage}>&#9658;</button>
      </div>
    </div>
  );
}

function debounce(func, wait) {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
}

export default Chat;